// Forked from cc-frontend/lib/ai/ai-stream-parser, which parses the legacy response schema.
// A unified lesson's cards carry no standards of their own (standards live inside a card's
// value, as widgets), so there is no standardIds command here — and no original lesson to
// resolve card ids against, since the editor holds every card.

import { parsePartialJSON } from "cc-frontend/lib/ai/partial-json-parser"

/** The assistant's prose so far, accumulated across its chat blocks. */
export interface ChatAppendCommand {
  type: "chatAppend"
  text: string
}

export interface SetTitleCommand {
  type: "setTitle"
  title: string
}

/** The card's whole new value, as Markdown. Streamed: `value` grows until `isDone`. */
export interface SetCardValueCommand {
  type: "setCardValue"
  id: string
  value: string
  isDone: boolean
}

export type AiStreamCommand = ChatAppendCommand | SetTitleCommand | SetCardValueCommand

type AiResponse = Array<
  | {
      type: "chat"
      text?: string
    }
  | {
      type: "changeTitle"
      title?: string
    }
  | {
      type: "changeCard"
      id?: string
      value?: { isDone: boolean; value: string }
      changes?: string
    }
>

/**
 * Incrementally parses a streaming response from our lesson-editing AI assistant
 * (backend Spikey.Ai.Lesson, unified response schema).
 */
export class AiStreamParser {
  private accumulatedDelta = ""
  private previousParsedResponse: AiResponse = []

  /**
   * Accumulate chat text for the whole response in case the AI splits it
   * over multiple "chat" blocks.
   */
  private accumulatedChatText = ""

  /**
   * Apply a delta received from the assistant, returning the commands that can be
   * completed so far.
   */
  applyDelta(delta: string): AiStreamCommand[] {
    if (delta === "") return []

    this.accumulatedDelta += delta
    let currentParsedResponse: AiResponse
    try {
      currentParsedResponse = parsePartialJSON(
        this.accumulatedDelta,
        // Don't return the fields we key decisions on until they've parsed completely.
        // Card values are marked with isDone instead, so they can stream.
        (keyName) => {
          if (keyName === "value") return "mark"
          else return !["type", "id", "title", "changes"].includes(keyName)
        },
      ) as AiResponse
    } catch {
      // If parsing fails, return empty array and wait for more data.
      return []
    }

    const commands: AiStreamCommand[] = []
    for (let i = 0; i < currentParsedResponse.length; i++) {
      const currentItem = currentParsedResponse[i]
      const previousItem = this.previousParsedResponse[i]
      if (!currentItem) continue

      if (currentItem.type === "chat") {
        const prevChatItem = previousItem?.type === "chat" ? previousItem : undefined
        commands.push(...this.processChatItem(currentItem, prevChatItem))
      } else if (currentItem.type === "changeTitle") {
        const prevChangeTitleItem = previousItem?.type === "changeTitle" ? previousItem : undefined
        if (currentItem.title !== undefined && prevChangeTitleItem?.title === undefined) {
          commands.push({ type: "setTitle", title: currentItem.title })
        }
      } else if (currentItem.type === "changeCard") {
        const prevChangeCardItem = previousItem?.type === "changeCard" ? previousItem : undefined
        commands.push(...this.processChangeCardItem(currentItem, prevChangeCardItem))
      }
    }

    this.previousParsedResponse = currentParsedResponse
    return commands
  }

  private processChatItem(
    currentItem: { type: "chat"; text?: string },
    previousItem: { type: "chat"; text?: string } | undefined,
  ): AiStreamCommand[] {
    if (currentItem.text === undefined) return []

    const currentText = currentItem.text || ""
    const previousText = previousItem?.text
    if (previousText !== undefined && currentText === previousText) return []

    if (previousText === undefined && this.accumulatedChatText !== "") {
      // The AI split its response across multiple "chat" blocks.
      // Combine with Markdown newline.
      this.accumulatedChatText += "\n\n"
    }
    this.accumulatedChatText += currentText.slice(previousText?.length || 0)

    return [{ type: "chatAppend", text: this.accumulatedChatText }]
  }

  private processChangeCardItem(
    currentItem: {
      type: "changeCard"
      id?: string
      value?: { isDone: boolean; value: string }
    },
    previousItem:
      | {
          type: "changeCard"
          id?: string
          value?: { isDone: boolean; value: string }
        }
      | undefined,
  ): AiStreamCommand[] {
    if (currentItem.id === undefined || currentItem.value === undefined) return []

    const currentValue = currentItem.value.value || ""
    const previousValue = previousItem?.value?.value
    // Re-issue the final value even when it hasn't grown, so its isDone reaches the editor.
    if (
      previousValue === currentValue &&
      previousItem?.value?.isDone === currentItem.value.isDone
    ) {
      return []
    }

    return [
      {
        type: "setCardValue",
        id: currentItem.id,
        value: currentValue,
        isDone: currentItem.value.isDone,
      },
    ]
  }
}
